import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Globe, 
  Package, 
  TrendingDown, 
  ShieldCheck, 
  Truck, 
  Users,
  Factory,
  Leaf,
  Pickaxe,
  Building2,
  Heart
} from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      icon: Package,
      title: 'Procurement Outsourcing',
      description: 'Complete management of your purchasing process from sourcing to delivery.',
      featured: false,
    },
    {
      icon: Globe,
      title: 'International Sourcing',
      description: 'Global supplier network with competitive pricing and quality assurance.',
      featured: true,
    },
    {
      icon: Users,
      title: 'Supplier Identification',
      description: 'Expert vetting and selection of reliable suppliers worldwide.',
      featured: false,
    },
    {
      icon: TrendingDown,
      title: 'Order Management',
      description: 'Streamlined order processing and tracking systems.',
      featured: false,
    },
    {
      icon: Truck,
      title: 'Logistics Coordination',
      description: 'End-to-end logistics management and customs clearance.',
      featured: false,
    },
    {
      icon: Factory,
      title: 'Industrial Supply',
      description: 'Specialized sourcing for industrial and agro-industrial sectors.',
      featured: false,
    },
  ];

  const sectors = [
    { icon: Factory, name: 'Industry' },
    { icon: Leaf, name: 'Agro-industry' },
    { icon: Pickaxe, name: 'Mining' },
    { icon: Building2, name: 'Construction' },
    { icon: Heart, name: 'NGO & Institutions' },
  ];

  const stats = [
    { value: '500+', label: 'Suppliers Network' },
    { value: '30+', label: 'Countries Served' },
    { value: '98%', label: 'Client Satisfaction' },
    { value: '24/7', label: 'Support Available' },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen bg-[#0F172A] flex items-center noise-overlay grid-pattern"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A]"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <span className="inline-block text-[#0EA5E9] text-sm font-medium uppercase tracking-widest mb-6">
              Procurement Excellence
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Your Trusted <br />
              <span className="text-[#0EA5E9]">Procurement Partner</span><br />
              in Africa and Beyond
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed mb-8 max-w-lg">
              Supply Hub simplifies international sourcing and secures every step of 
              the purchasing process. Specialized in DRC, Central Africa and emerging markets.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button
                  data-testid="hero-request-quote-btn"
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-none px-8 py-6 font-medium text-base transition-colors duration-300 group"
                >
                  Request a Quote
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </Link>
              <Link to="/services">
                <Button
                  data-testid="hero-services-btn"
                  variant="outline"
                  className="border-white/20 text-white bg-transparent rounded-none px-8 py-6 hover:bg-white hover:text-[#0F172A] transition-colors duration-300"
                >
                  Our Services
                </Button>
              </Link>
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="hidden lg:block animate-fade-in animation-delay-200">
            <div className="relative">
              <div className="absolute -inset-4 border border-[#0EA5E9]/20 -z-10"></div>
              <img
                src="/6.jpeg"
                alt="Global Logistics Hub - Supply Hub"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                data-testid={`stat-${index}`}
                className="text-center animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="font-['Outfit'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-2">
                  {stat.value}
                </div>
                <div className="text-slate-500 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - Bento Grid */}
      <section data-testid="services-section" className="bg-[#F8FAFC] py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              What We Do
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-5xl font-semibold text-[#0F172A] mt-4 mb-4">
              Our Services
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Comprehensive procurement solutions tailored to your industry needs
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-testid={`service-card-${index}`}
                className={`group bg-white border border-slate-200 p-8 hover:border-[#0EA5E9] hover:shadow-lg transition-all duration-300 ${
                  service.featured ? 'lg:col-span-2 lg:row-span-1' : ''
                }`}
              >
                <div className={`w-12 h-12 flex items-center justify-center mb-6 ${
                  service.featured ? 'bg-[#0EA5E9]' : 'bg-[#0F172A]'
                }`}>
                  <service.icon className="text-white" size={24} />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-[#0F172A] mb-3 group-hover:text-[#0EA5E9] transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
                {service.featured && (
                  <Link
                    to="/services"
                    className="inline-flex items-center text-[#0EA5E9] font-medium mt-4 group/link"
                  >
                    Learn More
                    <ArrowRight className="ml-2 group-hover/link:translate-x-1 transition-transform" size={16} />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-none px-8 py-4 font-medium transition-colors duration-300"
              >
                View All Services
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section data-testid="sectors-section" className="bg-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
                Industries We Serve
              </span>
              <h2 className="font-['Outfit'] text-3xl md:text-5xl font-semibold text-[#0F172A] mt-4 mb-6">
                Sectors of Activity
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We serve diverse industries across Africa and beyond, providing tailored 
                procurement solutions that meet the unique requirements of each sector.
              </p>
              <Link to="/sectors">
                <Button
                  data-testid="explore-sectors-btn"
                  className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white rounded-none px-8 py-4 font-medium transition-colors duration-300"
                >
                  Explore Sectors
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {sectors.map((sector, index) => (
                <Link
                  key={sector.name}
                  to="/sectors"
                  data-testid={`sector-card-${index}`}
                  className="group bg-[#F8FAFC] border border-slate-200 p-6 text-center hover:bg-[#0F172A] hover:border-[#0F172A] transition-all duration-300"
                >
                  <sector.icon className="w-8 h-8 mx-auto mb-3 text-[#0F172A] group-hover:text-[#0EA5E9] transition-colors" />
                  <span className="text-sm font-medium text-[#0F172A] group-hover:text-white transition-colors">
                    {sector.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-us-section" className="bg-[#0F172A] py-20 md:py-32 noise-overlay">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#0EA5E9] text-sm font-medium uppercase tracking-widest">
              Our Advantage
            </span>
            <h2 className="font-['Outfit'] text-3xl md:text-5xl font-semibold text-white mt-4 mb-4">
              Why Choose Supply Hub
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Strategic advantages that set us apart in the procurement industry
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Globe,
                title: 'Global Network',
                description: 'Access to an extensive international supplier network across continents.',
              },
              {
                icon: TrendingDown,
                title: 'Cost Reduction',
                description: 'Significant savings through strategic sourcing and negotiation.',
              },
              {
                icon: ShieldCheck,
                title: 'DRC & African Expertise',
                description: 'Deep understanding of DRC, Central Africa and emerging markets.',
              },
              {
                icon: Package,
                title: 'End-to-End Service',
                description: 'Complete management from sourcing to final delivery.',
              },
            ].map((item, index) => (
              <div
                key={item.title}
                data-testid={`benefit-card-${index}`}
                className="group bg-[#1E293B] border border-slate-700 p-8 hover:border-[#0EA5E9] transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#0EA5E9]/10 flex items-center justify-center mb-6 group-hover:bg-[#0EA5E9] transition-colors">
                  <item.icon className="text-[#0EA5E9] group-hover:text-white transition-colors" size={24} />
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/why-us">
              <Button
                data-testid="learn-more-btn"
                variant="outline"
                className="border-white/20 text-white bg-transparent rounded-none px-8 py-4 hover:bg-white hover:text-[#0F172A] transition-colors duration-300"
              >
                Learn More About Us
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="bg-[#0EA5E9] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold text-white mb-4">
            Ready to Optimize Your Procurement?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Let us help you streamline your supply chain and reduce costs with our 
            expert procurement services.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-contact-btn"
                className="bg-white text-[#0EA5E9] hover:bg-[#F8FAFC] rounded-none px-8 py-4 font-medium transition-colors duration-300"
              >
                Contact Us Today
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/services">
              <Button
                data-testid="cta-services-btn"
                variant="outline"
                className="border-white text-white bg-transparent rounded-none px-8 py-4 hover:bg-white/10 transition-colors duration-300"
              >
                View Services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
